/**
 * Two charts, drawn by hand.
 *
 * A charting library would be about 100 kB to draw a bar per over and one
 * line — more than the entire tournament screen, on the tab a scorer opens
 * mid-match. These are plain SVG: no dependency, no layout thrash, and they
 * scale with the card because they are viewBox'd rather than measured.
 *
 * Both are decoration over numbers that are already on the screen, so neither
 * carries information you cannot get elsewhere. They exist because shape is
 * faster to read than a column of figures when you are looking for where a
 * match turned.
 */

export interface OverPoint { over: number; runs: number; wickets: number }

const AMBER = "#E9A13B";
const LEATHER = "#D7625A";
const LINE = "#232A31";
const FAINT = "#7E8793";

/**
 * Runs per over, with wickets marked.
 *
 * The classic manhattan. Wickets are a dot above the bar rather than a colour
 * change, because a bar that is both "a big over" and "an over with a wicket"
 * cannot say both things with one fill.
 */
export function Manhattan({ overs, className = "" }: {
  overs: OverPoint[]; className?: string;
}) {
  if (overs.length === 0) return null;
  const max = Math.max(6, ...overs.map((o) => o.runs));
  const W = 100, H = 34, gap = 0.6;
  const bw = W / overs.length - gap;

  return (
    <svg viewBox={`0 0 ${W} ${H + 6}`} className={`w-full ${className}`}
         role="img" aria-label={`Runs per over. Highest ${max}.`}>
      {/* a baseline, so a maiden reads as zero rather than as missing */}
      <line x1="0" y1={H} x2={W} y2={H} stroke={LINE} strokeWidth="0.4" />
      {overs.map((o, i) => {
        const h = (o.runs / max) * (H - 4);
        const x = i * (bw + gap);
        return (
          <g key={o.over}>
            <rect x={x} y={H - h} width={bw} height={h} rx="0.6"
                  fill={o.runs >= max * 0.75 ? AMBER : "#3A4550"} />
            {Array.from({ length: o.wickets }, (_, k) => (
              <circle key={k} cx={x + bw / 2} cy={H - h - 2 - k * 2.4} r="0.9" fill={LEATHER} />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * How the run rate moved.
 *
 * Cumulative, not per-over: the question this answers is "are they ahead of
 * where they need to be", and a per-over rate is too noisy to show that. When
 * a target exists it is drawn as the required rate, which is the only line
 * anyone actually watches in a chase.
 */
export function RunRate({ overs, target, totalOvers, className = "" }: {
  overs: OverPoint[]; target?: number | null; totalOvers?: number; className?: string;
}) {
  if (overs.length < 2) return null;

  let running = 0;
  const pts = overs.map((o, i) => {
    running += o.runs;
    return { x: i + 1, rate: running / (i + 1) };
  });

  const need = target && totalOvers ? target / totalOvers : null;
  const max = Math.max(...pts.map((p) => p.rate), need ?? 0) * 1.15 || 1;
  const W = 100, H = 30;
  const px = (x: number) => ((x - 1) / Math.max(1, pts.length - 1)) * W;
  const py = (r: number) => H - (r / max) * H;

  const d = pts.map((p, i) => `${i ? "L" : "M"}${px(p.x).toFixed(1)},${py(p.rate).toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={`w-full ${className}`}
         role="img" aria-label={`Run rate, now ${pts[pts.length - 1]!.rate.toFixed(2)}`}>
      {need !== null && (
        <line x1="0" y1={py(need)} x2={W} y2={py(need)}
              stroke={FAINT} strokeWidth="0.4" strokeDasharray="2 2" />
      )}
      <path d={d} fill="none" stroke={AMBER} strokeWidth="1.2"
            strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={px(pts[pts.length - 1]!.x)} cy={py(pts[pts.length - 1]!.rate)} r="1.4" fill={AMBER} />
    </svg>
  );
}
